import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ShieldCheck, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { FeedbacksReportsResponse, FeedbackWeekData } from '../../types/reports';
import { processFeedbacksToWeekly } from '../../utils/feedbackReportUtils';
import { KPICardSkeleton } from '../ui/Skeleton';
import { EmptyValue } from '../ui/EmptyValue';
import { SessionFeedbackModal } from './SessionFeedbackModal';
import { NPSModal } from './NPSModal';

interface EmployeeSatisfactionProps {
  feedbackData: FeedbacksReportsResponse | null;
  comparisonData?: FeedbacksReportsResponse | null;
  loading?: boolean;
}

interface SatisfactionInsights {
  sessionSatisfactionRate: number | null; // Average overall rating (1-10 scale)
  sessionSatisfactionTrend: number; // Absolute change in satisfaction points
  npsScore: number; // Net Promoter Score (-100 to +100)
  npsTrend: number; // Absolute change in NPS points
  // Previous values for tooltips
  previousSessionSatisfactionRate: number | null;
  previousNpsScore: number | null;
}

const useSatisfactionInsights = (
  sessionWeeks: FeedbackWeekData[], 
  feedbackData: FeedbacksReportsResponse | null,
  comparisonData?: FeedbacksReportsResponse | null
): SatisfactionInsights | null => {
  if (!sessionWeeks.length || !feedbackData) return null;
  
  const latestWeek = sessionWeeks[sessionWeeks.length - 1];
  const previousWeek = sessionWeeks.length > 1 ? sessionWeeks[sessionWeeks.length - 2] : undefined;
  
  // Session Satisfaction Rate (Overall Rating)
  const currentOverall = latestWeek?.sessionFeedbacks.find(f => f.key === 'overallRating')?.average ?? null;
  const previousOverall = previousWeek?.sessionFeedbacks.find(f => f.key === 'overallRating')?.average ?? null;
  
  // Calculate trend against comparison period if available
  let overallTrend = 0;
  let previousOverallRating = null;
  if (comparisonData) {
    const comparisonRatings = comparisonData.feedbacks
      .map(f => f.feedbackData.overallRating)
      .filter((rating): rating is number => typeof rating === 'number');
    const comparisonAvg = comparisonRatings.length 
      ? comparisonRatings.reduce((sum, r) => sum + r, 0) / comparisonRatings.length 
      : null;
    
    previousOverallRating = comparisonAvg;
    if (currentOverall != null && comparisonAvg != null) {
      overallTrend = currentOverall - comparisonAvg; // Show absolute difference in rating points
    }
  } else if (currentOverall != null && previousOverall != null) {
    previousOverallRating = previousOverall;
    overallTrend = currentOverall - previousOverall; // Show absolute difference in rating points
  }
  
  // NPS calculation from Friend Rating (typically 1-10 scale)
  // NPS standard: 9-10 = Promoters, 7-8 = Passives, 0-6 = Detractors
  // Assuming 10-point scale: 9-10 = Promoters, 7-8 = Passives, 1-6 = Detractors
  const friendRatings = feedbackData.feedbacks
    .map(f => f.feedbackData.ratingFriends)
    .filter((rating): rating is number => typeof rating === 'number' && rating >= 1 && rating <= 10);
  
  let npsScore = 0;
  if (friendRatings.length > 0) {
    const promoters = friendRatings.filter(r => r >= 9).length;
    const detractors = friendRatings.filter(r => r <= 6).length;
    npsScore = ((promoters - detractors) / friendRatings.length) * 100;
  }
  
  // Calculate NPS trend against comparison period
  let npsTrend = 0;
  let previousNpsScore = null;
  if (comparisonData) {
    const comparisonFriendRatings = comparisonData.feedbacks
      .map(f => f.feedbackData.ratingFriends)
      .filter((rating): rating is number => typeof rating === 'number' && rating >= 1 && rating <= 10);
    
    if (comparisonFriendRatings.length > 0) {
      const comparisonPromoters = comparisonFriendRatings.filter(r => r >= 9).length;
      const comparisonDetractors = comparisonFriendRatings.filter(r => r <= 6).length;
      const comparisonNps = ((comparisonPromoters - comparisonDetractors) / comparisonFriendRatings.length) * 100;
      
      previousNpsScore = comparisonNps;
      // Calculate trend as absolute difference for NPS (since NPS can be negative)
      npsTrend = npsScore - comparisonNps;
    }
  }
  
  return {
    sessionSatisfactionRate: currentOverall,
    sessionSatisfactionTrend: overallTrend,
    npsScore,
    npsTrend,
    previousSessionSatisfactionRate: previousOverallRating,
    previousNpsScore,
  };
};

const EmployeeSatisfaction: React.FC<EmployeeSatisfactionProps> = ({ 
  feedbackData, 
  comparisonData,
  loading = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNPSModalOpen, setIsNPSModalOpen] = useState(false);
  
  const { sessionWeeks } = useMemo(() => {
    if (!feedbackData) {
      return { sessionWeeks: [] as FeedbackWeekData[], appWeeks: [] as FeedbackWeekData[] };
    }

    return processFeedbacksToWeekly(feedbackData);
  }, [feedbackData]);

  const satisfactionInsights = useSatisfactionInsights(sessionWeeks, feedbackData, comparisonData);

  // Show skeleton loading state
  if (loading) {
    return (
      <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-full bg-emerald-50 p-2 text-emerald-600">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Employees Satisfaction</h2>
        </div>
        
        <div className="space-y-4">
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      </div>
    );
  }

  // Show empty state when no data
  if (!feedbackData || !satisfactionInsights) {
    return (
      <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-full bg-emerald-50 p-2 text-emerald-600">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold text-rm-primary-700">Employees Satisfaction</h2>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4">
            <div>
              <p className="text-sm font-medium text-rm-ink-900">Session Satisfaction Rate</p>
              <p className="text-xs text-rm-neutral-600">Overall rating</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-rm-ink-900">
                <EmptyValue size="lg" />
                <span className="ml-1 text-sm font-normal text-rm-neutral-600">/ 10</span>
              </p>
              <p className="text-xs text-rm-neutral-600 italic">No data changes</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4">
            <div>
              <p className="text-sm font-medium text-rm-ink-900">Net Promoter Score (NPS)</p>
              <p className="text-xs text-rm-neutral-600">How likely are you to recommend Kim's service to a colleague</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-semibold text-rm-ink-900">
                <EmptyValue size="lg" />
              </p>
              <p className="text-xs text-rm-neutral-600 italic">No data changes</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-full bg-emerald-50 p-2 text-emerald-600">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-semibold text-rm-primary-700">Employees Satisfaction</h2>
      </div>
      
      <div className="space-y-4">
        <div 
          className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4 cursor-pointer hover:ring-2 hover:ring-rm-accent-500/30 transition-all"
          onClick={() => setIsModalOpen(true)}
        >
          <div>
            <p className="text-sm font-medium text-rm-ink-900">Session Satisfaction Rate</p>
            <p className="text-xs text-rm-neutral-600">Overall rating</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold text-rm-ink-900">
              {satisfactionInsights.sessionSatisfactionRate?.toFixed(1) ?? '—'}
              <span className="ml-1 text-sm font-normal text-rm-neutral-600">/ 10</span>
            </p>
            {comparisonData ? (
              satisfactionInsights.sessionSatisfactionTrend !== 0 ? (
                <div 
                  className={clsx(
                    'inline-flex items-center gap-1 text-xs font-semibold',
                    satisfactionInsights.sessionSatisfactionTrend >= 0 ? 'text-emerald-600' : 'text-red-600'
                  )}
                  title={`Previous: ${satisfactionInsights.previousSessionSatisfactionRate?.toFixed(1) ?? 'N/A'}/10 (${comparisonData.summary?.dateRange?.startDate} - ${comparisonData.summary?.dateRange?.endDate})`}
                >
                  {satisfactionInsights.sessionSatisfactionTrend >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {satisfactionInsights.sessionSatisfactionTrend >= 0 ? '+' : ''}{satisfactionInsights.sessionSatisfactionTrend.toFixed(1)} pts
                </div>
              ) : (
                <p className="text-xs text-rm-neutral-600">No data changes</p>
              )
            ) : (
              <p className="text-xs text-rm-neutral-600 italic">No data changes</p>
            )}
          </div>
        </div>
        
        <div 
          className="flex items-center justify-between rounded-card bg-rm-bg-50 p-4 cursor-pointer hover:ring-2 hover:ring-rm-accent-500/30 transition-all"
          onClick={() => setIsNPSModalOpen(true)}
        >
          <div>
            <p className="text-sm font-medium text-rm-ink-900">Net Promoter Score (NPS)</p>
            <p className="text-xs text-rm-neutral-600">How likely are you to recommend Kim's service to a colleague</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold text-rm-ink-900">
              {satisfactionInsights.npsScore.toFixed(0)}
            </p>
            {comparisonData ? (
              satisfactionInsights.npsTrend !== 0 ? (
                <div 
                  className={clsx(
                    'inline-flex items-center gap-1 text-xs font-semibold',
                    satisfactionInsights.npsTrend >= 0 ? 'text-emerald-600' : 'text-red-600'
                  )}
                  title={`Previous NPS: ${satisfactionInsights.previousNpsScore?.toFixed(0) ?? 'N/A'} (${comparisonData.summary?.dateRange?.startDate} - ${comparisonData.summary?.dateRange?.endDate})`}
                >
                  {satisfactionInsights.npsTrend >= 0 ? (
                    <ArrowUpRight className="h-3 w-3" />
                  ) : (
                    <ArrowDownRight className="h-3 w-3" />
                  )}
                  {satisfactionInsights.npsTrend >= 0 ? '+' : ''}{satisfactionInsights.npsTrend.toFixed(0)} pts
                </div>
              ) : (
                <p className="text-xs text-rm-neutral-600">No data changes</p>
              )
            ) : (
              <p className="text-xs text-rm-neutral-600 italic">No data changes</p>
            )}
          </div>
        </div>
      </div>

      {/* Session Feedback Detail Modal */}
      {feedbackData && (
        <SessionFeedbackModal
          data={feedbackData}
          comparisonData={comparisonData}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {/* NPS Detail Modal */}
      {feedbackData && (
        <NPSModal
          data={feedbackData}
          comparisonData={comparisonData}
          isOpen={isNPSModalOpen}
          onClose={() => setIsNPSModalOpen(false)}
        />
      )}
    </div>
  );
};

export default EmployeeSatisfaction;